import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import Points from 'components/Points';
import { AssetUrls } from 'config/assets';
import { useBulkBanUsersMutation } from 'store/services/admin/adminApi';
import { BulkBanResponse } from 'types/interfaces/admin/bulkBan';
import {
  BULK_BAN_DEFAULT_REASON,
  BULK_BAN_REASON_MAX,
  BULK_BAN_REASON_MIN,
  BULK_BAN_REASON_OTHER,
  BULK_BAN_REASON_PRESETS,
  BULK_BAN_REQUEST_FAILED_MESSAGE,
  bulkBanFlagLabel,
  bulkBanRowNote,
  isFlaggedBannable,
  resolveBulkBanReason,
  sortBulkBanPreview,
} from './bulkBan';
import { showBulkBanResultToast } from './BulkBanResultToast';
import './BulkBanModal.css';

interface BulkBanModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The selected user ids, in table order. */
  userIds: number[];
  /** Called after an apply call returns, whatever the per-id outcomes were. */
  onCompleted: () => void;
}

const formatJoinDate = (iso: string | null): string => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const BulkBanModal: React.FC<BulkBanModalProps> = ({ isOpen, onClose, userIds, onCompleted }) => {
  const [runPreview, { isLoading: isPreviewLoading }] = useBulkBanUsersMutation();
  const [runApply, { isLoading: isApplying }] = useBulkBanUsersMutation();

  const [preview, setPreview] = useState<BulkBanResponse | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [reasonChoice, setReasonChoice] = useState<string>(BULK_BAN_DEFAULT_REASON);
  const [otherText, setOtherText] = useState('');
  const [reviewed, setReviewed] = useState(false);

  // One dryRun per open. The preview does not depend on the reason, so it is
  // not refetched when the reason changes. The backend runs the same validation
  // in both modes and discards a dryRun reason, so the default preset is sent
  // as a placeholder.
  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setPreviewFailed(false);
      setReasonChoice(BULK_BAN_DEFAULT_REASON);
      setOtherText('');
      setReviewed(false);
      return;
    }
    let cancelled = false;
    runPreview({ userIds, reason: BULK_BAN_DEFAULT_REASON, dryRun: true })
      .unwrap()
      .then((response) => {
        if (!cancelled) setPreview(response);
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // userIds is captured at open; the table cannot change selection under an open modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const rows = useMemo(() => (preview ? sortBulkBanPreview(preview.results) : []), [preview]);

  if (!isOpen) return null;

  const banCount = preview ? preview.summary.wouldBan : userIds.length;
  const hasFlagged = rows.some(isFlaggedBannable);
  const reason = resolveBulkBanReason(reasonChoice, otherText);
  const otherLength = otherText.trim().length;
  const canConfirm =
    Boolean(preview) && banCount > 0 && reason !== null && (!hasFlagged || reviewed) && !isApplying;

  const handleConfirm = async () => {
    if (!canConfirm || reason === null) return;
    try {
      const response = await runApply({ userIds, reason, dryRun: false }).unwrap();
      showBulkBanResultToast(response);
      onCompleted();
      onClose();
    } catch (err: any) {
      // A 429 is already toasted by the global handler in baseQuery; a second
      // toast here would only repeat it. The apply mutation invalidates the user
      // list on error too, so the table refetches and shows what was banned.
      if (err?.status !== 429) {
        toast.error(err?.data?.message || BULK_BAN_REQUEST_FAILED_MESSAGE);
      }
    }
  };

  const accountsLabel = banCount === 1 ? 'account' : 'accounts';

  const modalContent = (
    <div className="bulk-ban-modal-overlay" onClick={isApplying ? undefined : onClose}>
      <div className="bulk-ban-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="bulk-ban-modal-header">
          <h2 className="bulk-ban-modal-title">
            Ban {banCount} {accountsLabel}
          </h2>
          <button className="bulk-ban-close-button" onClick={onClose} disabled={isApplying} aria-label="Close">
            <img src={AssetUrls.icons.dropdowns.close} alt="" style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div className="bulk-ban-modal-content">
          <div className="bulk-ban-field">
            <label className="bulk-ban-label" htmlFor="bulk-ban-reason">
              Reason
            </label>
            <select
              id="bulk-ban-reason"
              className="bulk-ban-select"
              value={reasonChoice}
              onChange={(e) => setReasonChoice(e.target.value)}
              disabled={isApplying}
            >
              {BULK_BAN_REASON_PRESETS.map((preset) => (
                <option key={preset} value={preset}>
                  {preset}
                </option>
              ))}
              <option value={BULK_BAN_REASON_OTHER}>{BULK_BAN_REASON_OTHER}</option>
            </select>
            {reasonChoice === BULK_BAN_REASON_OTHER ? (
              <>
                <textarea
                  className="bulk-ban-textarea"
                  aria-label="Ban reason"
                  placeholder="Describe the reason for this ban"
                  rows={3}
                  maxLength={BULK_BAN_REASON_MAX}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  disabled={isApplying}
                />
                {otherLength > 0 && otherLength < BULK_BAN_REASON_MIN ? (
                  <p className="bulk-ban-hint-error">Reason must be at least {BULK_BAN_REASON_MIN} characters</p>
                ) : null}
              </>
            ) : null}
          </div>

          {isPreviewLoading && !preview ? <p className="bulk-ban-status">Loading preview...</p> : null}
          {previewFailed ? (
            <p className="bulk-ban-status bulk-ban-status-error">
              Could not load the preview. Close this window and try again. Nothing was banned.
            </p>
          ) : null}

          {preview ? (
            <>
              <div className="bulk-ban-table-wrapper">
                <table className="bulk-ban-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Join Date</th>
                      <th>Points</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const flagged = isFlaggedBannable(row);
                      const excluded = row.outcome !== 'would_ban';
                      const rowClass = flagged
                        ? 'bulk-ban-row-flagged'
                        : excluded
                        ? 'bulk-ban-row-excluded'
                        : undefined;
                      return (
                        <tr key={row.id} className={rowClass} data-outcome={row.outcome}>
                          <td>{row.username ?? `#${row.id}`}</td>
                          <td className="bulk-ban-cell-email">{row.email ?? '-'}</td>
                          <td>{formatJoinDate(row.createdAt)}</td>
                          <td>{row.points === null ? '-' : <Points type="small" points={row.points} />}</td>
                          <td>
                            {excluded ? (
                              <span className="bulk-ban-note">Will not be banned: {bulkBanRowNote(row)}</span>
                            ) : row.flags.length > 0 ? (
                              <span className="bulk-ban-flags">
                                {row.flags.map((flag) => (
                                  <span key={flag} className="bulk-ban-flag">
                                    {bulkBanFlagLabel(flag)}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="bulk-ban-legend">
                Flagged accounts have points, verification, trades, or a later login. Review before banning.
              </p>

              {hasFlagged ? (
                <label className="bulk-ban-review">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    onChange={(e) => setReviewed(e.target.checked)}
                    disabled={isApplying}
                  />
                  <span>I've reviewed the flagged accounts</span>
                </label>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="bulk-ban-modal-footer">
          <MagicButton label="Cancel" color="grey" onPress={onClose} isDisabled={isApplying} fullWidth={false} />
          <MagicButton
            label={`Ban ${banCount} ${accountsLabel}`}
            color="default"
            onPress={handleConfirm}
            isLoading={isApplying}
            isDisabled={!canConfirm}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BulkBanModal;
